// AddTeamMemberProfileForm.tsx
import React, { useEffect, useState } from "react";
import { jsonGet } from "../utils/http";
import { buildApiUrl } from "../utils/apiConfig";
import { useNavigate } from "react-router-dom";
import { getTermsAccepted } from "../utils/auth";
import "../styles/forms.css";
import RoleSelect from "./RoleSelect";
import CoachSportsField from "./CoachSportsField";
import TeamMemberConfigurations from "./TeamMemberConfigurations";

export type Profile = {
    firstName: string;
    lastName: string;
    email: string;
    title: string;
    role: string;        // display name
    roleId?: number;     // numeric id from options
    telephone: string;
    sendInvite?: boolean;
    sportId?: number;
    sportIds?: number[];
    internalCoachPreReadEnabled?: boolean;
    manageStudentAthletesEnabled?: boolean;
};

export type ProfileFormProps = {
    loadUrl: string;                 // GET returns a "view" with profileTypes[]
    saveUrl: string;                 // POST accepts Profile payload
    useCookies?: boolean;
    onSaved?: (saved: Profile) => void;
    onClose?: () => void;
    afterSaveTo?: string | ((saved: Profile) => string);
};

type RoleOption = { id: number; name: string };

// shape of the "view" endpoint you showed
type ViewPayload = {
    states?: Array<{ id: number; name: string }>;
    profileTypes?: RoleOption[];
};

export default function AddTeamMemberProfileForm({
                                                     loadUrl,
                                                     saveUrl,
                                                     useCookies = false,
                                                     onSaved,
                                                     onClose,
                                                     afterSaveTo,
                                                 }: ProfileFormProps) {
    const navigate = useNavigate();

    const [data, setData] = useState<Profile>({
        firstName: "",
        lastName: "",
        email: "",
        title: "",
        role: "",          // will be set from profileTypes
        roleId: undefined, // will be set from profileTypes
        telephone: "",
        sendInvite: true,
        sportId: undefined,
        sportIds: [],
        internalCoachPreReadEnabled: false,
        manageStudentAthletesEnabled: false,
    });

    const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState<string>("");
    const [ok, setOk] = useState<string>("");
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Profile, string>>>({});

    // Load the view (profileTypes, states, etc.) and seed the dropdown
    useEffect(() => {
        // Don't load options if terms haven't been accepted yet
        const termsAccepted = getTermsAccepted();
        console.log('AddTeamMemberProfileForm: Load check - termsAccepted:', termsAccepted);

        if (!termsAccepted) {
            console.log('AddTeamMemberProfileForm: Skipping load - terms not accepted yet');
            setLoading(false);
            return;
        }

        console.log('AddTeamMemberProfileForm: Proceeding with load - terms accepted');
        let cancelled = false;

        (async () => {
            setLoading(true);
            setServerError("");
            try {
                const view = await jsonGet<ViewPayload>(loadUrl, useCookies);
                const opts: RoleOption[] = Array.isArray(view?.profileTypes) ? view!.profileTypes! : [];

                if (cancelled) return;

                setRoleOptions(opts);

                // pick the first available option as default
                if (opts.length > 0) {
                    setData((d) => ({
                        ...d,
                        role: d.role || opts[0].name,
                        roleId: d.roleId ?? opts[0].id,
                    }));
                }
            } catch (e: any) {
                if (!cancelled) setServerError(e?.message || "Failed to load form options.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [loadUrl, useCookies]);

    const set =
        (k: keyof Profile) =>
            (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                setData((s) => ({ ...s, [k]: e.target.value }));

    async function onSave() {
        setServerError("");
        setOk("");

        const errors: Partial<Record<keyof Profile, string>> = {};

        if (!data.firstName.trim()) {
            errors.firstName = "First name is required.";
        }
        if (!data.lastName.trim()) {
            errors.lastName = "Last name is required.";
        }
        if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
            errors.email = "Please enter a valid email address.";
        }
        if (!data.title.trim()) {
            errors.title = "Title is required.";
        }
        if (!data.telephone.trim()) {
            errors.telephone = "Phone number is required.";
        } else {
            // Accepts US numbers in formats like: (555) 555-5555, 555-555-5555,
            // 555.555.5555, 5555555555, +1 555 555 5555, 1-555-555-5555
            const usPhoneRegex = /^(\+?1[\s.\-]?)?(\(?\d{3}\)?[\s.\-]?)?\d{3}[\s.\-]?\d{4}$/;
            if (!usPhoneRegex.test(data.telephone.trim())) {
                errors.telephone = "Please enter a valid US phone number (e.g. (555) 555-5555).";
            }
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setFieldErrors({});

        try {
            const checkRes = await fetch(
                buildApiUrl(
                    `api/subscriptions/check-email?email=${encodeURIComponent(data.email.trim())}`
                )
            );
            if (checkRes.ok) {
                const checkJson = (await checkRes.json()) as { available?: boolean };
                if (checkJson.available === false) {
                    setFieldErrors({ email: "This email is already registered." });
                    return;
                }
            }
        } catch {
            // If availability check fails, proceed; server enforces uniqueness on POST
        }

        // ensure role & roleId are synced with options
        if (roleOptions.length > 0) {
            const match =
                roleOptions.find((o) => o.id === data.roleId) ||
                roleOptions.find((o) => o.name.toLowerCase() === (data.role || "").toLowerCase());
            if (match) {
                data.role = match.name;
                data.roleId = match.id;
            } else {
                // fallback to first option if somehow out of sync
                data.role = roleOptions[0].name;
                data.roleId = roleOptions[0].id;
            }
        }

        setSaving(true);
        try {
            const res = await fetch(saveUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                credentials: "include", // Send HTTP-only cookies
                body: JSON.stringify({
                    ...data,
                    sportIds: data.sportIds ?? [],
                    sportId: (data.sportIds ?? [])[0] ?? null,
                    internalCoachPreReadEnabled: !!data.internalCoachPreReadEnabled,
                    manageStudentAthletesEnabled: !!data.manageStudentAthletesEnabled,
                }),
            });

            // Handle error responses with user-friendly messages
            if (!res.ok) {
                try {
                    const errorData = await res.json();
                    const errorMessage = errorData.detail || errorData.message || `An unexpected error has occurred`;
                    setServerError(errorMessage);
                    return;
                } catch (parseError) {
                    // If response body can't be parsed, use generic error
                    setServerError(`An unexpected error has occurred`);
                    return;
                }
            }

            setOk("Profile saved.");
            onSaved?.(data);

            if (afterSaveTo) {
                const target = typeof afterSaveTo === "function" ? afterSaveTo(data) : afterSaveTo;
                if (target) {
                    navigate(target);
                    return;
                }
            }
            onClose?.();
        } catch (e: any) {
            setServerError(e?.message || "Failed to save.");
        } finally {
            setSaving(false);
        }
    }

    function onCancel() {
        onClose?.();
        // Only navigate if not in modal context (when afterSaveTo is not provided)
        if (!onClose) {
            navigate("/team");
        }
    }

    return (
        <section className="card form-card profile-card" aria-busy={loading}>
            <div className="form-head">
                <h2 className="form-title">Add team member</h2>
            </div>

            {(serverError || ok) && (
                <div className={`form-alert ${serverError ? "is-error" : "is-ok"}`}>{serverError || ok}</div>
            )}

            <div className="grid-2">
                <div className="field">
                    <label>FIRST NAME*</label>
                    <input
                        className={`input${fieldErrors.firstName ? " input-error" : ""}`}
                        value={data.firstName}
                        onChange={(e) => { set("firstName")(e); setFieldErrors((fe) => ({ ...fe, firstName: undefined })); }}
                    />
                    {fieldErrors.firstName && <span className="field-error">{fieldErrors.firstName}</span>}
                </div>

                <div className="field">
                    <label>LAST NAME*</label>
                    <input
                        className={`input${fieldErrors.lastName ? " input-error" : ""}`}
                        value={data.lastName}
                        onChange={(e) => { set("lastName")(e); setFieldErrors((fe) => ({ ...fe, lastName: undefined })); }}
                    />
                    {fieldErrors.lastName && <span className="field-error">{fieldErrors.lastName}</span>}
                </div>

                <div className="field">
                    <label>EMAIL*</label>
                    <input
                        className={`input${fieldErrors.email ? " input-error" : ""}`}
                        type="email"
                        value={data.email}
                        onChange={(e) => { set("email")(e); setFieldErrors((fe) => ({ ...fe, email: undefined })); }}
                    />
                    {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
                </div>

                <div className="field">
                    <label>TITLE*</label>
                    <input
                        className={`input${fieldErrors.title ? " input-error" : ""}`}
                        value={data.title}
                        onChange={(e) => { set("title")(e); setFieldErrors((fe) => ({ ...fe, title: undefined })); }}
                    />
                    {fieldErrors.title && <span className="field-error">{fieldErrors.title}</span>}
                </div>

                <div className="field">
                    <label>ACCOUNT ACCESS*</label>
                    <div className="select-wrap">
                        <RoleSelect
                            options={roleOptions}
                            valueId={data.roleId ?? null}
                            valueName={data.role ?? null}
                            onChange={(opt) => setData((s) => ({
                                ...s,
                                role: opt.name,
                                roleId: opt.id,
                                internalCoachPreReadEnabled: false,
                                manageStudentAthletesEnabled: false,
                                ...(opt.name.toLowerCase() === "coach" ? {} : { sportIds: [], sportId: undefined }),
                            }))}
                        />
                    </div>
                </div>

                <div className="field">
                    <label>PHONE NUMBER*</label>
                    <input
                        className={`input${fieldErrors.telephone ? " input-error" : ""}`}
                        value={data.telephone}
                        onChange={(e) => { set("telephone")(e); setFieldErrors((fe) => ({ ...fe, telephone: undefined })); }}
                    />
                    {fieldErrors.telephone && <span className="field-error">{fieldErrors.telephone}</span>}
                </div>

                {(data.role || "").toLowerCase() === "coach" && (
                    <CoachSportsField
                        sportIds={data.sportIds ?? []}
                        onChange={(sportIds) => setData((s) => ({ ...s, sportIds, sportId: sportIds[0] }))}
                    />
                )}

                <TeamMemberConfigurations
                    role={data.role}
                    internalCoachPreReadEnabled={!!data.internalCoachPreReadEnabled}
                    manageStudentAthletesEnabled={!!data.manageStudentAthletesEnabled}
                    onInternalCoachPreReadChange={(enabled) =>
                        setData((s) => ({ ...s, internalCoachPreReadEnabled: enabled }))
                    }
                    onManageStudentAthletesChange={(enabled) =>
                        setData((s) => ({ ...s, manageStudentAthletesEnabled: enabled }))
                    }
                />

                {/* Unified row: checkbox (left) + actions (right) */}
                <div className="form-actions-row">
                    <label className="check-inline">
                        <input
                            type="checkbox"
                            className="check-input"
                            checked={!!data.sendInvite}
                            onChange={(e) => setData((s) => ({ ...s, sendInvite: e.target.checked }))}
                        />
                        <span className="check-label">
              Send an email to the team member to provide access.
            </span>
                    </label>

                    <div className="actions-right">
                        <button type="button" className="btn-cancel" onClick={onCancel}>
                            Cancel
                        </button>
                        <button
                            className="btn-pill"
                            onClick={onSave}
                            disabled={saving || loading}
                            aria-busy={saving}
                        >
                            {saving ? "Saving…" : "Add team member"}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}